import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { motion } from 'framer-motion';
import {
  TrendingUp, TrendingDown, Scale, Wallet, PlusCircle, PieChart, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const GOLD = '#C0975C';
const serif = { fontFamily: 'Outfit, sans-serif' };

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 0 }).format(
    amount || 0
  );

const fieldWrap =
  'flex items-center gap-2 bg-[#061530] border border-[#C0975C]/25 px-4 py-3 rounded-lg focus-within:border-[#C0975C] transition-colors';
const inputCls = 'w-full bg-transparent text-white outline-none text-lg font-semibold';
const labelCls = 'block text-xs uppercase tracking-wider text-[#9fb0cc] mb-2';

const Panel = ({ title, icon: Icon, children, subtitle }) => (
  <div className="border border-[#1D3D72] bg-[#122C5A] p-6 sm:p-8 rounded-2xl shadow-xl shadow-black/20">
    <div className="flex items-center gap-2 mb-1">
      {Icon && <Icon className="w-5 h-5 text-[#C0975C]" />}
      <h2 className="text-xl font-bold" style={serif}>{title}</h2>
    </div>
    {subtitle && <p className="text-sm text-[#9fb0cc] mb-5">{subtitle}</p>}
    {!subtitle && <div className="mb-5" />}
    {children}
  </div>
);

const SummaryCard = ({ label, value, icon: Icon, accent, note }) => (
  <div className="border border-[#1D3D72] bg-[#122C5A] p-6 rounded-2xl shadow-xl shadow-black/20">
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`w-5 h-5 ${accent ? 'text-[#C0975C]' : 'text-[#9fb0cc]'}`} />
      <p className="text-xs uppercase tracking-wider text-[#9fb0cc]">{label}</p>
    </div>
    <p className={`text-3xl font-bold ${accent ? 'text-[#C0975C]' : 'text-white'}`} style={serif}>
      {value}
    </p>
    {note && <p className="text-sm mt-1 text-[#9fb0cc]">{note}</p>}
  </div>
);

const BudgetTracker = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('income');
  const [categories, setCategories] = useState({ income_categories: [], expense_categories: [] });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const [incomeForm, setIncomeForm] = useState({
    source: '', amount: '', date: new Date().toISOString().split('T')[0], notes: ''
  });
  const [expenseForm, setExpenseForm] = useState({
    category: '', amount: '', date: new Date().toISOString().split('T')[0], notes: ''
  });

  useEffect(() => {
    loadCategories();
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/budget/categories`, authHeader);
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/budget/summary`, authHeader);
      setSummary(response.data);
      if (response.data.streak_reward > 0) {
        toast.success('Seven day tracking streak reached. Your Financial Fitness Score is climbing.');
      }
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  const handleAddIncome = async (e) => {
    e.preventDefault();
    if (!incomeForm.source || !incomeForm.amount) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/budget/income`, incomeForm, authHeader);
      toast.success(response.data.message);
      setIncomeForm({ source: '', amount: '', date: new Date().toISOString().split('T')[0], notes: '' });
      loadSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add income');
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!expenseForm.category || !expenseForm.amount) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/budget/expense`, expenseForm, authHeader);
      toast.success(response.data.message);
      setExpenseForm({ category: '', amount: '', date: new Date().toISOString().split('T')[0], notes: '' });
      loadSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add expense');
    } finally {
      setLoading(false);
    }
  };

  const isIncome = activeTab === 'income';
  const currentCats = isIncome ? categories.income_categories : categories.expense_categories;
  const catValue = isIncome ? incomeForm.source : expenseForm.category;

  return (
    <Layout>
      <div className="p-6 lg:p-8 max-w-6xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#C0975C] flex items-center justify-center rounded-lg">
              <Wallet className="w-7 h-7 text-[#0A1F44]" />
            </div>
            <div>
              <h1 className="text-3xl font-bold" style={serif}>
                Cashflow <span className="text-[#C0975C]">Overview</span>
              </h1>
              <p className="text-[#9fb0cc]">Record your income and expenses to see where your money goes</p>
            </div>
          </div>
        </motion.div>

        {/* Summary */}
        {summary && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8"
          >
            <SummaryCard label="Total Income" value={formatCurrency(summary.total_income)} icon={TrendingUp} />
            <SummaryCard label="Total Expenses" value={formatCurrency(summary.total_expenses)} icon={TrendingDown} />
            <SummaryCard
              label="Net Balance"
              value={formatCurrency(summary.net_balance)}
              icon={Scale}
              accent
              note={summary.net_balance >= 0 ? 'You are on track.' : 'Room to tighten up.'}
            />
          </motion.div>
        )}

        {/* Tabs */}
        <div className="flex gap-3 mb-6">
          <button
            onClick={() => setActiveTab('income')}
            data-testid="tab-income"
            className={`flex-1 flex items-center justify-center gap-2 h-14 text-lg font-semibold rounded-lg transition-all ${
              isIncome ? 'bg-[#C0975C] text-[#0A1F44]' : 'bg-white/5 text-white hover:bg-white/10'
            }`}
          >
            <TrendingUp className="w-5 h-5" /> Add Income
          </button>
          <button
            onClick={() => setActiveTab('expense')}
            data-testid="tab-expense"
            className={`flex-1 flex items-center justify-center gap-2 h-14 text-lg font-semibold rounded-lg transition-all ${
              !isIncome ? 'bg-[#C0975C] text-[#0A1F44]' : 'bg-white/5 text-white hover:bg-white/10'
            }`}
          >
            <TrendingDown className="w-5 h-5" /> Add Expense
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form */}
          <motion.div key={activeTab} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
            <Panel title={isIncome ? 'Add Income' : 'Add Expense'} icon={PlusCircle}
              subtitle={isIncome ? 'Log your income sources' : 'Track where your money goes'}>
              <form onSubmit={isIncome ? handleAddIncome : handleAddExpense} className="space-y-5">
                <div>
                  <label className={labelCls}>{isIncome ? 'Income Source' : 'Expense Category'}</label>
                  <div className={fieldWrap}>
                    <select
                      value={catValue}
                      onChange={(e) =>
                        isIncome
                          ? setIncomeForm({ ...incomeForm, source: e.target.value })
                          : setExpenseForm({ ...expenseForm, category: e.target.value })
                      }
                      className={`${inputCls} appearance-none cursor-pointer`}
                      data-testid="budget-category"
                    >
                      <option value="" className="bg-[#0A1F44]">Select {isIncome ? 'source' : 'category'}</option>
                      {currentCats.map((cat) => (
                        <option key={cat} value={cat} className="bg-[#0A1F44]">{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Amount</label>
                  <div className={fieldWrap}>
                    <span className="text-[#C0975C] font-semibold">R</span>
                    <input
                      type="number"
                      placeholder="0"
                      value={isIncome ? incomeForm.amount : expenseForm.amount}
                      onChange={(e) =>
                        isIncome
                          ? setIncomeForm({ ...incomeForm, amount: e.target.value })
                          : setExpenseForm({ ...expenseForm, amount: e.target.value })
                      }
                      className={inputCls}
                      data-testid="budget-amount"
                    />
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Date</label>
                  <div className={fieldWrap}>
                    <input
                      type="date"
                      value={isIncome ? incomeForm.date : expenseForm.date}
                      onChange={(e) =>
                        isIncome
                          ? setIncomeForm({ ...incomeForm, date: e.target.value })
                          : setExpenseForm({ ...expenseForm, date: e.target.value })
                      }
                      className={`${inputCls} [color-scheme:dark]`}
                      data-testid="budget-date"
                    />
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Notes (Optional)</label>
                  <div className={fieldWrap}>
                    <input
                      type="text"
                      placeholder="Add a note"
                      value={isIncome ? incomeForm.notes : expenseForm.notes}
                      onChange={(e) =>
                        isIncome
                          ? setIncomeForm({ ...incomeForm, notes: e.target.value })
                          : setExpenseForm({ ...expenseForm, notes: e.target.value })
                      }
                      className={inputCls}
                      data-testid="budget-notes"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-14 text-lg font-semibold rounded-lg bg-[#C0975C] text-[#0A1F44] hover:opacity-90 disabled:opacity-50 transition-opacity"
                  data-testid="budget-submit"
                >
                  {loading ? 'Saving...' : isIncome ? 'Add Income' : 'Add Expense'}
                </button>
              </form>
            </Panel>
          </motion.div>

          {/* Breakdown */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
            <Panel title="Category Breakdown" icon={PieChart}
              subtitle={summary?.month ? `For ${new Date(summary.month).toLocaleString('en-ZA', { month: 'long', year: 'numeric' })}` : 'Current month'}>
              {summary && (
                <div className="space-y-6">
                  {Object.keys(summary.income_by_category).length > 0 && (
                    <div>
                      <h3 className="text-sm font-bold uppercase tracking-wider text-[#C0975C] mb-3 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" /> Income Sources
                      </h3>
                      <div className="space-y-2">
                        {Object.entries(summary.income_by_category).map(([category, amount]) => (
                          <div key={category} className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                            <span className="text-white">{category}</span>
                            <span className="text-[#C0975C] font-bold">{formatCurrency(amount)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {Object.keys(summary.expense_by_category).length > 0 && (
                    <div>
                      <h3 className="text-sm font-bold uppercase tracking-wider text-[#9fb0cc] mb-3 flex items-center gap-2">
                        <TrendingDown className="w-4 h-4" /> Expense Categories
                      </h3>
                      <div className="space-y-3">
                        {Object.entries(summary.expense_by_category)
                          .sort((a, b) => b[1] - a[1])
                          .map(([category, amount]) => {
                            const percentage = ((amount / summary.total_expenses) * 100).toFixed(1);
                            return (
                              <div key={category} className="space-y-1">
                                <div className="flex justify-between items-center">
                                  <span className="text-white text-sm">{category}</span>
                                  <span className="text-white font-bold">{formatCurrency(amount)}</span>
                                </div>
                                <div className="w-full bg-white/10 rounded-full h-2">
                                  <div className="bg-[#C0975C] h-2 rounded-full transition-all" style={{ width: `${percentage}%` }} />
                                </div>
                                <p className="text-xs text-[#9fb0cc]">{percentage}% of expenses</p>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {Object.keys(summary.income_by_category).length === 0 &&
                    Object.keys(summary.expense_by_category).length === 0 && (
                      <div className="text-center py-10 text-[#9fb0cc]">
                        <FileText className="w-10 h-10 mx-auto mb-3 text-[#C0975C]" />
                        <p>No entries yet. Start recording your cashflow.</p>
                      </div>
                    )}
                </div>
              )}
            </Panel>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default BudgetTracker;
